'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import CustomSelect from '@/components/ui/CustomSelect';
import { motion, AnimatePresence } from 'framer-motion';

interface FormData {
    name: string;
    email: string;
    mobile: string;
    company: string;
    domain: string;
    service: string;
    budget: string;
    overview: string;
}

interface FormErrors {
    [key: string]: string;
}

const ConsultationForm: React.FC = () => {
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        mobile: '',
        company: '',
        domain: '',
        service: '',
        budget: '',
        overview: ''
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const serviceOptions = [
        'Website Development',
        'Mobile App Development',
        'Brand Visibility (SEO/GBP)',
        'E-commerce Development',
        'UI/UX Design',
        'Complete Digital Package',
        'Other'
    ];

    const budgetOptions = [
        '₹30,000 - ₹50,000',
        '₹50,000 - ₹1,00,000',
        '₹1,00,000 - ₹2,50,000',
        '₹2,50,000 - ₹5,00,000',
        '₹5,00,000+',
        'Lets discuss in connect'
    ];

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }
        if (!formData.mobile.trim()) {
            newErrors.mobile = 'Mobile number is required';
        } else if (!/^[+]?[0-9]{10,15}$/.test(formData.mobile.replace(/\s/g, ''))) {
            newErrors.mobile = 'Please enter a valid mobile number';
        }
        if (!formData.service) newErrors.service = 'Please select a service';
        if (!formData.budget) newErrors.budget = 'Please select a budget range';
        if (!formData.overview.trim()) {
            newErrors.overview = 'Project overview is required';
        } else if (formData.overview.trim().length < 20) {
            newErrors.overview = 'Please provide at least 20 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            const response = await fetch('https://sitemapgenius.com/backend/contact.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    mobile: formData.mobile,
                    company: formData.company,
                    website: formData.domain,
                    service: formData.service,
                    budget: formData.budget,
                    message: formData.overview
                }),
            });

            const result = await response.json();

            if (result.success) {
                setSubmitStatus('success');
                setFormData({
                    name: '',
                    email: '',
                    mobile: '',
                    company: '',
                    domain: '',
                    service: '',
                    budget: '',
                    overview: ''
                });
                setTimeout(() => setSubmitStatus('idle'), 5000);
            } else {
                setSubmitStatus('error');
                console.error('Submission failed:', result.message);
                // Optionally handle error UI here
            }
        } catch (error) {
            setSubmitStatus('error');
            console.error('Submission error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section id="consultation-form" className="py-20 bg-slate-50">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="bg-white rounded-[2rem] shadow-2xl overflow-hidden flex flex-col lg:flex-row min-h-[700px]"
                >
                    {/* Left: Creative Visual Panel */}
                    <div className="lg:w-2/5 bg-slate-900 relative p-12 text-white flex flex-col justify-between overflow-hidden">
                        {/* Decorative Background Elements */}
                        <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
                            <div className="absolute top-[-20%] left-[-20%] w-[80%] h-[80%] bg-indigo-600 rounded-full blur-3xl opacity-50 animate-pulse-slow"></div>
                            <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] bg-blue-500 rounded-full blur-3xl opacity-40"></div>
                            <div className="absolute top-[40%] right-[-20%] w-[40%] h-[40%] bg-purple-500 rounded-full blur-3xl opacity-30"></div>
                        </div>

                        {/* Content */}
                        <div className="relative z-10">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs font-bold uppercase tracking-wider mb-8">
                                <span className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse"></span>
                                Currently Accepting New Projects
                            </div>

                            <h2 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
                                Let's Build <br />
                                <span className="text-indigo-400">Something Great</span> <br />
                                Together by <br />
                                <span className="text-slate-400 font-medium text-3xl">Scheduling a Connect</span>
                            </h2>

                            <p className="text-slate-300 text-lg leading-relaxed max-w-md">
                                Share your vision with us. We'll analyze your requirements and craft a detailed roadmap to bring your ideas to life.
                            </p>
                        </div>

                        {/* Trust Indicators */}
                        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 gap-6 mt-12">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-indigo-400">
                                    <Icon name="ClockIcon" size={20} />
                                </div>
                                <div>
                                    <div className="font-bold">24-48h</div>
                                    <div className="text-xs text-slate-400">Response Time</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-indigo-400">
                                    <Icon name="ShieldCheckIcon" size={20} />
                                </div>
                                <div>
                                    <div className="font-bold">Strict NDA</div>
                                    <div className="text-xs text-slate-400">Your Idea is Safe</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: Form Panel */}
                    <div className="lg:w-3/5 p-8 md:p-12 lg:p-16 bg-white">
                        <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto">
                            {/* Mobile Header (Only visible on small screens) */}
                            <div className="lg:hidden mb-8">
                                <h3 className="text-2xl font-bold text-slate-900">Project Details</h3>
                                <p className="text-slate-500">Tell us about what you want to build.</p>
                            </div>

                            {/* Row 1: Name & Email */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                                        Full Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${errors.name ? 'border-red-500' : 'border-slate-300'
                                            }`}
                                        placeholder="Abdul Sohail"
                                    />
                                    {errors.name && (
                                        <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                                        Email Address <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${errors.email ? 'border-red-500' : 'border-slate-300'
                                            }`}
                                        placeholder="abdul@sitemapgenius.com"
                                    />
                                    {errors.email && (
                                        <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                                    )}
                                </div>
                            </div>

                            {/* Row 2: Mobile & Company */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="mobile" className="block text-sm font-semibold text-slate-700 mb-2">
                                        Mobile Number <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        id="mobile"
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${errors.mobile ? 'border-red-500' : 'border-slate-300'
                                            }`}
                                        placeholder="+91 8686284396"
                                    />
                                    {errors.mobile && (
                                        <p className="mt-1 text-sm text-red-500">{errors.mobile}</p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="company" className="block text-sm font-semibold text-slate-700 mb-2">
                                        Company Name
                                    </label>
                                    <input
                                        type="text"
                                        id="company"
                                        name="company"
                                        value={formData.company}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                        placeholder="Your Company (optional)"
                                    />
                                </div>
                            </div>

                            {/* Row 3: Domain Address */}
                            <div>
                                <label htmlFor="domain" className="block text-sm font-semibold text-slate-700 mb-2">
                                    Current Website / Domain
                                </label>
                                <input
                                    type="url"
                                    id="domain"
                                    name="domain"
                                    value={formData.domain}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                    placeholder="https://yourwebsite.com (optional)"
                                />
                            </div>

                            {/* Row 4: Service & Budget */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <CustomSelect
                                        label="Service Needed"
                                        required
                                        options={serviceOptions}
                                        value={formData.service}
                                        onChange={(value) => {
                                            setFormData(prev => ({ ...prev, service: value }));
                                            if (errors.service) setErrors(prev => ({ ...prev, service: '' }));
                                        }}
                                        error={errors.service}
                                        placeholder="Select a service"
                                    />
                                </div>

                                <div>
                                    <CustomSelect
                                        label="Estimated Budget"
                                        required
                                        options={budgetOptions}
                                        value={formData.budget}
                                        onChange={(value) => {
                                            setFormData(prev => ({ ...prev, budget: value }));
                                            if (errors.budget) setErrors(prev => ({ ...prev, budget: '' }));
                                        }}
                                        error={errors.budget}
                                        placeholder="Select budget range"
                                    />
                                </div>
                            </div>

                            {/* Row 5: Project Overview */}
                            <div>
                                <label htmlFor="overview" className="block text-sm font-semibold text-slate-700 mb-2">
                                    Project Overview <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    id="overview"
                                    name="overview"
                                    value={formData.overview}
                                    onChange={handleChange}
                                    rows={5}
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none ${errors.overview ? 'border-red-500' : 'border-slate-300'
                                        }`}
                                    placeholder="Tell us about your project, goals, and any specific requirements..."
                                />
                                {errors.overview && (
                                    <p className="mt-1 text-sm text-red-500">{errors.overview}</p>
                                )}
                                <p className="mt-1 text-xs text-slate-500">
                                    {formData.overview.length} / 500 characters
                                </p>
                            </div>

                            {/* Submit Button */}
                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-indigo-600 text-white font-bold py-4 px-6 rounded-lg hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-500/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg hover:shadow-indigo-500/30 hover:-translate-y-0.5"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Icon name="PaperAirplaneIcon" size={20} />
                                            Submit Consultation Request
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* Success/Error Messages */}
                            <AnimatePresence>
                                {submitStatus === 'success' && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg"
                                    >
                                        <Icon name="CheckCircleIcon" size={24} className="text-green-600" />
                                        <div>
                                            <p className="font-semibold text-green-900">Request Submitted!</p>
                                            <p className="text-sm text-green-700">
                                                We'll review your request and get back to you within 24-48 hours.
                                            </p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </form>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default ConsultationForm;